import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { RotateCcw, Undo2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return '-'; const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleDateString('es-AR'); };

/**
 * v3.37.3 — Lista de aplicaciones de anticipo con botón "Revertir" por fila.
 * Soporta proveedores (entityType='supplier') y residentes (entityType='resident').
 */
export function AdvanceApplicationsSection({ entityType, entityId, refreshKey, onReversed }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [reversing, setReversing] = useState(false);

  const listUrl = entityType === 'supplier'
    ? `${BACKEND_URL}/api/supplier-advance-applications?supplier_id=${entityId}`
    : `${BACKEND_URL}/api/resident-advance-applications?resident_id=${entityId}`;

  const reverseUrl = (appId) => entityType === 'supplier'
    ? `${BACKEND_URL}/api/supplier-advance-applications/${appId}/reverse`
    : `${BACKEND_URL}/api/resident-advance-applications/${appId}/reverse`;

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const r = await axios.get(listUrl, { withCredentials: true });
      setItems(r.data?.items || []);
    } catch { toast.error('Error al cargar aplicaciones de anticipo'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityId, refreshKey]);

  // v3.47.13 — Las imputaciones automáticas de recibo (collection_auto) NO son
  // anticipos: el comprobante existía antes y el recibo lo canceló. Se ocultan de
  // este panel; la cancelación ya queda reflejada en la cuenta corriente.
  const visibleItems = items.filter(a => a.source_type !== 'collection_auto');

  const confirmReverse = async () => {
    if ((reason || '').trim().length < 10) { toast.error('El motivo debe tener al menos 10 caracteres'); return; }
    setReversing(true);
    try {
      await axios.post(reverseUrl(target.application_id), { reason: reason.trim() }, { withCredentials: true });
      toast.success(`Aplicación revertida (${fmtMoney(target.applied_amount)})`);
      setTarget(null); setReason('');
      await load();
      onReversed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al revertir');
    } finally { setReversing(false); }
  };

  if (!visibleItems.length && !loading) {
    return <p className="text-xs text-muted-foreground italic" data-testid="aa-empty">Sin aplicaciones de anticipo registradas desde v3.37.3.</p>;
  }

  return (
    <div className="space-y-2" data-testid={`aa-section-${entityType}`}>
      <div className="flex items-center gap-2">
        <RotateCcw className="w-4 h-4 text-indigo-600" />
        <Label className="font-semibold">Aplicaciones de anticipo</Label>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Fecha</TableHead>
              <TableHead className="text-xs">Comprobante</TableHead>
              <TableHead className="text-right text-xs">Aplicado</TableHead>
              <TableHead className="text-xs">Origen</TableHead>
              <TableHead className="text-xs">Estado</TableHead>
              <TableHead className="text-right text-xs w-28">Acción</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleItems.map(a => (
              <TableRow key={a.application_id} data-testid={`aa-row-${a.application_id}`}>
                <TableCell className="text-xs">{fmtDate(a.applied_at)}</TableCell>
                <TableCell className="text-xs font-mono">{a.invoice_label || '-'}</TableCell>
                <TableCell className="text-right text-xs font-mono">{fmtMoney(a.applied_amount)}</TableCell>
                <TableCell className="text-xs">
                  {(a.consumption_detail || []).map((c, i) => (
                    <div key={i} className="font-mono text-[11px] flex items-center gap-1">
                      {c.source_pdf_url ? (
                        <button
                          type="button"
                          onClick={() => window.open(`${BACKEND_URL}${c.source_pdf_url}`, '_blank')}
                          className="text-indigo-700 hover:text-indigo-900 hover:underline inline-flex items-center gap-1"
                          title="Abrir comprobante en PDF"
                          data-testid={`aa-pdf-link-${a.application_id}-${i}`}
                        >
                          {c.source_number}
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      ) : (
                        <span>{c.source_number}</span>
                      )}
                      <span className="text-muted-foreground">· {fmtMoney(c.amount)}</span>
                    </div>
                  ))}
                </TableCell>
                <TableCell className="text-xs">
                  {a.reversed_at
                    ? <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-300">Revertida</Badge>
                    : (a.source_type === 'collection_auto' || a.source_type === 'payment_order_auto')
                      ? <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200" title={a.source_type === 'collection_auto' ? 'Imputación automática del recibo' : 'Imputación automática de la OP'}>
                          {a.source_type === 'collection_auto' ? 'Automática (recibo)' : 'Automática (OP)'}
                        </Badge>
                      : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Activa</Badge>}
                  {a.reversed_at && <div className="text-[10px] text-muted-foreground mt-1">{fmtDate(a.reversed_at)} · {a.reversed_by_name}</div>}
                </TableCell>
                <TableCell className="text-right">
                  {!a.reversed_at && a.source_type !== 'collection_auto' && a.source_type !== 'payment_order_auto' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-amber-700 border-amber-200 hover:bg-amber-50"
                      onClick={() => setTarget(a)}
                      data-testid={`aa-reverse-btn-${a.application_id}`}
                    >
                      <Undo2 className="w-3 h-3 mr-1" />Revertir
                    </Button>
                  )}
                  {!a.reversed_at && (a.source_type === 'collection_auto' || a.source_type === 'payment_order_auto') && (
                    <span className="text-[10px] text-muted-foreground italic" title={a.source_type === 'collection_auto' ? 'Para revertir, eliminá el recibo desde Tesorería' : 'Para revertir, eliminá la OP desde Tesorería'}>
                      {a.source_type === 'collection_auto' ? 'Revertir desde el recibo' : 'Revertir desde la OP'}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!target} onOpenChange={(o) => { if (!o) { setTarget(null); setReason(''); } }}>
        <DialogContent data-testid="aa-reverse-dialog">
          <DialogHeader>
            <DialogTitle>Revertir aplicación de anticipo</DialogTitle>
            <DialogDescription>
              Se devolverán <b>{fmtMoney(target?.applied_amount)}</b> al saldo a favor y bajará el pago de <b>{target?.invoice_label}</b> en el mismo monto. Esta acción queda auditada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm">Motivo (mínimo 10 caracteres)</Label>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              placeholder="Describí el motivo de la reversa..."
              data-testid="aa-reverse-reason"
            />
            <div className="text-xs text-muted-foreground">{(reason || '').trim().length} / 10</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTarget(null); setReason(''); }} disabled={reversing}>Cancelar</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={confirmReverse}
              disabled={reversing || (reason || '').trim().length < 10}
              data-testid="aa-reverse-confirm"
            >
              {reversing ? 'Revirtiendo…' : <><Undo2 className="w-4 h-4 mr-1" />Confirmar reversa</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
